import { defineStore } from 'pinia'
import {
  checkUserExist,
  getNotifications,
  getTags,
  loginUsingOTP,
  onAuthChange,
  sendOTPApi
} from '@/model/global/Login'
import { computed, ref } from '@vue/composition-api'
import { acceptInvite, getBLForUser } from '@/model/dataLayer/api/Vip/InviteApi'
import GlobalConfig, { requireOfflineMode } from '@/model/global/GlobalSettings'
import { useStore } from '@/model/dataLayer/api/SaaS/storeService'
import { gotoLastRoute } from '@/model/dataLayer/api/SaaS/user'
import router from '@/model/global/router'

export const useUserStore = defineStore('user-store', () => {
  const currentUser = ref(null)
  const shouldShowLoginForm = ref(false)
  const formReady = ref(false)
  const currentCredit = ref(0)
  const showAddCredit = ref(false)
  const otpError = ref(null)
  const selectedTagId = ref(0)
  const tagList = ref([])
  const bindBl = ref(false)

  // Initialize with remembered email if available
  const emailInput = ref(localStorage.getItem('rememberedEmail') || '')

  onAuthChange(async ({
    event,
    token,
    userInfo
  }) => {
    currentUser.value = userInfo
    bindBl.value = await getBLForUser(userInfo.id)
    GlobalConfig.blType = !requireOfflineMode() ? bindBl?.value[0]?.type : ''
    shouldShowLoginForm.value = token === null
    if (!shouldShowLoginForm.value) {
      if (bindBl.value.length > 0) {
        const deviceId = bindBl.value[0].deviceId.toString()
        GlobalConfig.DeviceId = deviceId
        await useStore(deviceId, false)
        await gotoLastRoute(GlobalConfig.blType)
      } else {
        await router.push({ name: 'AcceptPage' })
      }
    } else {
      await router.replace({ name: 'LoginPage' })
    }
    await reload()
  })

  function validateEmail (input) {
    return !!String(input)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ) || 'The email address you entered is incorrect.'
  }

  const loginFormLoading = ref(true)
  const otpSent = ref(false)
  const otpInput = ref('')
  const userNotExists = ref(false)
  const loading = ref(false)

  async function sendOTP () {
    loading.value = true
    userNotExists.value = false
    otpError.value = null

    try {
      const userExist = await checkUserExist(emailInput.value)
      if (userExist) {
        try {
          otpSent.value = await sendOTPApi(emailInput.value)
          if (!otpSent.value) {
            throw new Error('Failed to send OTP')
          }
        } catch (e) {
          console.error('Error sending OTP:', e)
          otpError.value = 'Failed to send verification code. Please try again.'
        }
      } else {
        userNotExists.value = true
      }
    } catch (e) {
      console.error('Error checking user existence:', e)
      otpError.value = 'An error occurred. Please try again later.'
    } finally {
      loading.value = false
    }
  }

  async function acceptInviteByCode () {
    loading.value = true
    otpError.value = null

    try {
      const res = await acceptInvite(otpInput.value, currentUser.value.id)
      if (res.code === 500) {
        otpError.value = 'Failed to accept invitation. Please try again.'
      }
    } catch (e) {
      console.error('Error accepting invite:', e)
      otpError.value = 'Failed to accept invitation. Please try again.'
    } finally {
      loading.value = false
    }
  }

  async function acceptReset () {
    otpInput.value = ''
    otpError.value = null
    await router.replace({ name: 'LoginPage' })
  }

  async function login () {
    loading.value = true
    otpError.value = null

    try {
      await loginUsingOTP(emailInput.value, otpInput.value)
    } catch (e) {
      console.error('Login error:', e)
      otpError.value = 'Invalid verification code. Please try again.'
      otpInput.value = ''
    } finally {
      loading.value = false
    }
  }

  function reset () {
    // Don't clear email if it's remembered
    if (!localStorage.getItem('rememberedEmail')) {
      emailInput.value = ''
    }
    userNotExists.value = false
    otpInput.value = ''
    otpSent.value = false
    otpError.value = null
  }

  const articleList = ref([])
  const filteredArticleList = computed(() => {
    return articleList.value.filter(item => selectedTagId.value === 0 || item.workAnnouncementTags.some(tag => tag.id === selectedTagId.value))
  })

  async function reload () {
    tagList.value = [{ id: 0, name: 'Alle', description: '' }, ...await getTags()]
    selectedTagId.value = 0
    try {
      articleList.value = await getNotifications(currentUser.value.email)
    } catch (e) {
      console.log(e, 'e')
    }
    console.log(articleList.value, 'articleList.value')
  }

  return {
    acceptReset,
    acceptInviteByCode,
    bindBl,
    reload,
    filteredArticleList,
    articleList,
    selectedTagId,
    tagList,
    userNotExists,
    currentUser,
    emailInput,
    validateEmail,
    currentCredit,
    sendOTP,
    showAddCredit,
    reset,
    formReady,
    otpSent,
    shouldShowLoginForm,
    loginFormLoading,
    loading,
    otpInput,
    login,
    otpError
  }
})
